import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react"
import { toast } from "sonner"
import { useLocation } from "react-router-dom"
import { Ban, CheckCircle2, Loader2, RefreshCw, Search, ShieldCheck } from "lucide-react"
import { PageTitle } from "shared/components/layout/PageTitle"
import { Alert, AlertDescription } from "shared/ui/alert"
import { Button } from "shared/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "shared/ui/card"
import { Checkbox } from "shared/ui/checkbox"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "shared/ui/dialog"
import { Input } from "shared/ui/input"
import { Label } from "shared/ui/label"
import { Popover, PopoverContent, PopoverTrigger } from "shared/ui/popover"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "shared/ui/select"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "shared/ui/table"
import { Textarea } from "shared/ui/textarea"
import { getTenants, type Tenant } from "shared/api/tenantService"
import { DEFAULT_TENANT_ID, getPersistedSelectedSiteId, resolveTenantId } from "shared/utils/tenantContext"
import { searchRoles, updateUserRoles } from "modules/admin/api/roleService"
import { searchUsers, setUserActive, type UserAdminDto } from "modules/admin/api/userService"

type LayoutState = {
  user?: {
    id?: string
    userId?: string
    user_id?: string
    empId?: string
    emp_id?: string
    employeeId?: string
    employee_id?: string
  }
  site?: {
    id?: string
    name?: string
  }
}

const PAGE_SIZE_OPTIONS = ["10", "20", "50", "100"]
const AUTH_TOKEN_STORAGE_KEY = "eptw.jwt"
const CONTRACTOR_ROLE_SEARCH_TERM = (process.env.REACT_APP_CONTRACTOR_ROLE_CODE ?? "CONTRACTOR").trim()
const CONTRACTOR_ROLE_CODE = CONTRACTOR_ROLE_SEARCH_TERM.toLowerCase()

type StatusFilter = "all" | "active" | "inactive"

const formatDate = (value?: string | null) => {
  if (!value) return "-"
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return date.toLocaleString("th-TH", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  })
}

const resolveString = (...values: Array<string | null | undefined>) => {
  for (const value of values) {
    if (typeof value !== "string") continue
    const trimmed = value.trim()
    if (trimmed) return trimmed
  }
  return ""
}

const isGuidValue = (value?: string | null) =>
  typeof value === "string" &&
  /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(value.trim())

const decodeBase64Url = (value: string) => {
  const normalized = value.replace(/-/g, "+").replace(/_/g, "/")
  const padding = normalized.length % 4 === 0 ? "" : "=".repeat(4 - (normalized.length % 4))
  return atob(`${normalized}${padding}`)
}

const getUserIdFromToken = () => {
  try {
    const token = localStorage.getItem(AUTH_TOKEN_STORAGE_KEY)
    if (!token) return null
    const payloadRaw = token.split(".")[1]
    if (!payloadRaw) return null
    const payload = JSON.parse(decodeBase64Url(payloadRaw)) as Record<string, unknown>
    const candidates = [
      payload.UserId,
      payload.userId,
      payload.user_id,
      payload.EmpId,
      payload.empId,
      payload.emp_id,
      payload.employeeId,
      payload.employee_id,
    ]
    for (const candidate of candidates) {
      if (typeof candidate !== "string") continue
      if (isGuidValue(candidate)) return candidate.trim()
    }
  } catch {
    return null
  }
  return null
}

const resolveUserTenantIds = (user: UserAdminDto) =>
  [...(user.tenantIds ?? []), ...(user.tenantId ? [user.tenantId] : [])].filter(
    (value, index, arr) => Boolean(value) && arr.indexOf(value) === index,
  )

export function ApproveContractorPage() {
  const location = useLocation()
  const state = (location.state as LayoutState | undefined) ?? {}
  const persistedSiteId = useMemo(() => getPersistedSelectedSiteId(), [])
  const tenantId = resolveTenantId(state.site?.id, persistedSiteId)
  const currentUserId = useMemo(() => {
    const candidates: Array<string | null | undefined> = [
      state.user?.id,
      state.user?.userId,
      state.user?.user_id,
      state.user?.empId,
      state.user?.emp_id,
      state.user?.employeeId,
      state.user?.employee_id,
    ]

    for (const candidate of candidates) {
      if (!isGuidValue(candidate)) continue
      return candidate!.trim()
    }
    const fallbackFromToken = getUserIdFromToken()
    if (fallbackFromToken) return fallbackFromToken
    return null
  }, [state.user])

  const [items, setItems] = useState<UserAdminDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [keyword, setKeyword] = useState("")
  const [appliedKeyword, setAppliedKeyword] = useState("")
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("inactive")

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [totalItems, setTotalItems] = useState(0)
  const [totalPages, setTotalPages] = useState(1)

  const [tenants, setTenants] = useState<Tenant[]>([])
  const [isLoadingTenants, setIsLoadingTenants] = useState(false)
  const [selectedItem, setSelectedItem] = useState<UserAdminDto | null>(null)
  const [approveTenantIds, setApproveTenantIds] = useState<string[]>([])
  const [approveTenantSearch, setApproveTenantSearch] = useState("")
  const [isApproving, setIsApproving] = useState(false)
  const [rejectRemark, setRejectRemark] = useState("")
  const [isRejecting, setIsRejecting] = useState(false)
  const [inactiveItem, setInactiveItem] = useState<UserAdminDto | null>(null)
  const [inactiveRemark, setInactiveRemark] = useState("")
  const [isInactivating, setIsInactivating] = useState(false)

  const filteredTenants = useMemo(() => {
    const term = approveTenantSearch.trim().toLowerCase()
    if (!term) return tenants
    return tenants.filter((item) => `${item.name} ${item.tenantId}`.toLowerCase().includes(term))
  }, [approveTenantSearch, tenants])

  const selectedTenantsLabel = useMemo(() => {
    if (approveTenantIds.length === 0) return "Select tenant"
    const names = approveTenantIds
      .map((id) => tenants.find((item) => item.tenantId === id)?.name ?? id)
      .filter(Boolean)
    if (names.length <= 2) return names.join(", ")
    return `${names[0]}, ${names[1]} +${names.length - 2}`
  }, [approveTenantIds, tenants])

  const loadContractors = useCallback(async () => {
    if (!currentUserId) {
      setItems([])
      setTotalItems(0)
      setTotalPages(1)
      setError("Cannot resolve current user. Please reopen this page from menu.")
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const result = await searchUsers({
        tenantId,
        userType: "CONTRACTOR",
        profileMapping: currentUserId,
        isActive: statusFilter === "all" ? undefined : statusFilter === "active",
        search: appliedKeyword || undefined,
        page,
        pageSize,
      })

      setItems(result.items ?? [])
      setTotalItems(result.totalItems ?? 0)
      setTotalPages(result.totalPages ?? 1)
    } catch (err: any) {
      const message = err?.response?.data ?? err?.message ?? "Failed to load contractor list"
      setError(typeof message === "string" ? message : "Failed to load contractor list")
    } finally {
      setIsLoading(false)
    }
  }, [appliedKeyword, currentUserId, page, pageSize, statusFilter, tenantId])

  const loadTenants = useCallback(async () => {
    setIsLoadingTenants(true)
    try {
      const result = await getTenants()
      setTenants(result ?? [])
      // Default to all tenants selected once the full list is known - the initial
      // guess from the contractor's own record (set in handleOpenApproveDialog)
      // gets replaced by this as soon as it loads.
      setApproveTenantIds((result ?? []).map((item) => item.tenantId))
    } catch {
      setTenants([])
    } finally {
      setIsLoadingTenants(false)
    }
  }, [])

  useEffect(() => {
    void loadContractors()
  }, [loadContractors])

  useEffect(() => {
    if (!selectedItem) return
    void loadTenants()
  }, [loadTenants, selectedItem])

  const handleSearch = (event: FormEvent) => {
    event.preventDefault()
    setPage(1)
    setAppliedKeyword(keyword.trim())
  }

  const handleRefresh = () => {
    void loadContractors()
  }

  const handleClear = () => {
    setKeyword("")
    setAppliedKeyword("")
    setStatusFilter("inactive")
    setPage(1)
  }

  const handleOpenApproveDialog = (item: UserAdminDto) => {
    setSelectedItem(item)
    setApproveTenantIds(resolveUserTenantIds(item))
    setApproveTenantSearch("")
    setRejectRemark("")
  }

  const handleCloseApproveDialog = () => {
    setSelectedItem(null)
    setApproveTenantIds([])
    setApproveTenantSearch("")
    setRejectRemark("")
  }

  const handleApprove = async () => {
    if (!selectedItem) return
    if (approveTenantIds.length === 0) {
      toast.error("Please select at least one tenant before approve")
      return
    }

    setIsApproving(true)
    try {
      const roleCatalog = await searchRoles({
        search: CONTRACTOR_ROLE_SEARCH_TERM,
        page: 1,
        pageSize: 50,
        isActive: true,
      })
      const contractorRole = (roleCatalog.items ?? []).find((role) => {
        const code = (role.code ?? "").trim().toLowerCase()
        const displayName = (role.displayName ?? "").trim().toLowerCase()
        return code === CONTRACTOR_ROLE_CODE || displayName === "contractor"
      })

      if (!contractorRole?.roleId) {
        toast.error("Contractor role not found. Please configure role code in master data.")
        return
      }

      const primaryTenantId = approveTenantIds[0] ?? tenantId
      const mergedTenantIds = Array.from(new Set([primaryTenantId, ...approveTenantIds].filter(Boolean)))

      await setUserActive(selectedItem.userId, {
        isActive: true,
        remark: null,
        tenantId: primaryTenantId,
        tenantIds: mergedTenantIds,
      })

      await updateUserRoles(selectedItem.userId, [contractorRole.roleId])
      toast.success("Contractor approved")
      handleCloseApproveDialog()
      void loadContractors()
    } catch (err: any) {
      const message = err?.response?.data ?? err?.message ?? "Approve contractor failed"
      toast.error(typeof message === "string" ? message : "Approve contractor failed")
    } finally {
      setIsApproving(false)
    }
  }

  const handleReject = async () => {
    if (!selectedItem) return

    setIsRejecting(true)
    try {
      await setUserActive(selectedItem.userId, {
        isActive: false,
        remark: rejectRemark.trim() || null,
      })
      toast.success("Contractor request rejected")
      handleCloseApproveDialog()
      void loadContractors()
    } catch (err: any) {
      const message = err?.response?.data ?? err?.message ?? "Reject contractor failed"
      toast.error(typeof message === "string" ? message : "Reject contractor failed")
    } finally {
      setIsRejecting(false)
    }
  }

  const handleOpenInactiveDialog = (item: UserAdminDto) => {
    if (!item.isActive) return
    setInactiveItem(item)
    setInactiveRemark("")
  }

  const handleCloseInactiveDialog = () => {
    setInactiveItem(null)
    setInactiveRemark("")
  }

  const handleInactive = async () => {
    if (!inactiveItem) return
    setIsInactivating(true)
    try {
      await setUserActive(inactiveItem.userId, {
        isActive: false,
        remark: inactiveRemark.trim() || null,
      })
      toast.success("Contractor set to inactive")
      handleCloseInactiveDialog()
      void loadContractors()
    } catch (err: any) {
      const message = err?.response?.data ?? err?.message ?? "Inactive contractor failed"
      toast.error(typeof message === "string" ? message : "Inactive contractor failed")
    } finally {
      setIsInactivating(false)
    }
  }

  const startIndex = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
  const endIndex = totalItems === 0 ? 0 : Math.min(page * pageSize, totalItems)

  return (
    <div className="space-y-6">
      <PageTitle
        currentPage="approve-permit"
        titleOverride="Approve Contractor"
        descriptionOverride="Review contractor onboarding records and manage active/inactive status."
      />

      <Card className="border-blue-100">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center justify-between text-xl">
            <span className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-blue-600" />
              Contractor Approval
            </span>
            <Button variant="outline" onClick={handleRefresh} disabled={isLoading}>
              {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
              Refresh
            </Button>
          </CardTitle>
        </CardHeader>

        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <form onSubmit={handleSearch} className="grid gap-3 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Label htmlFor="contractor-keyword">Keyword</Label>
              <Input
                id="contractor-keyword"
                placeholder="username / email / full name / position / phone"
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                className="mt-1"
              />
            </div>

            <div className="lg:col-span-3">
              <Label htmlFor="status-filter">Status</Label>
              <Select
                value={statusFilter}
                onValueChange={(value) => {
                  setStatusFilter(value as StatusFilter)
                  setPage(1)
                }}
              >
                <SelectTrigger id="status-filter" className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive / Waiting</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end gap-2 lg:col-span-4 lg:justify-end">
              <Button type="button" variant="outline" onClick={handleClear}>
                Clear
              </Button>
              <Button type="submit">
                <Search className="mr-2 h-4 w-4" />
                Search
              </Button>
            </div>
          </form>

          <div className="overflow-x-auto rounded-lg border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Username</TableHead>
                  <TableHead>Full Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Position</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Company</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-sm text-gray-500">
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading...
                      </span>
                    </TableCell>
                  </TableRow>
                ) : items.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} className="py-8 text-center text-sm text-gray-500">
                      No records
                    </TableCell>
                  </TableRow>
                ) : (
                  items.map((item) => (
                    <TableRow key={item.userId}>
                      <TableCell>{resolveString(item.username, item.email, "-")}</TableCell>
                      <TableCell>{resolveString(item.fullName, "-")}</TableCell>
                      <TableCell>{resolveString(item.email, "-")}</TableCell>
                      <TableCell>{resolveString(item.position, "-")}</TableCell>
                      <TableCell>{resolveString(item.phone, "-")}</TableCell>
                      <TableCell>{resolveString(item.companyName, "-")}</TableCell>
                      <TableCell>
                        <span
                          className={
                            item.isActive
                              ? "rounded-full bg-emerald-100 px-2 py-1 text-xs font-semibold text-emerald-700"
                              : "rounded-full bg-amber-100 px-2 py-1 text-xs font-semibold text-amber-700"
                          }
                        >
                          {item.isActive ? "ACTIVE" : "WAITING"}
                        </span>
                      </TableCell>
                      <TableCell>{formatDate(item.createdAt)}</TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {!item.isActive ? (
                            <Button size="sm" onClick={() => handleOpenApproveDialog(item)}>
                              <CheckCircle2 className="mr-2 h-4 w-4" />
                              Approve
                            </Button>
                          ) : null}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleOpenInactiveDialog(item)}
                            disabled={!item.isActive}
                          >
                            <Ban className="mr-2 h-4 w-4" />
                            Inactive
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          <div className="flex flex-col gap-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
            <div>
              Showing {startIndex} - {endIndex} of {totalItems} records
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span>Page size</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    const next = Number.parseInt(value, 10)
                    if (Number.isNaN(next)) return
                    setPageSize(next)
                    setPage(1)
                  }}
                >
                  <SelectTrigger className="h-9 w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((prev) => Math.max(1, prev - 1))}>
                  Prev
                </Button>
                <span>
                  Page {page} / {Math.max(1, totalPages)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= Math.max(1, totalPages)}
                  onClick={() => setPage((prev) => Math.min(Math.max(1, totalPages), prev + 1))}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      <Dialog open={Boolean(selectedItem)} onOpenChange={(open) => !open && handleCloseApproveDialog()}>
        <DialogContent className="flex max-h-[90vh] flex-col sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Approve Contractor</DialogTitle>
            <DialogDescription>Review contractor information and select tenant assignments before approval.</DialogDescription>
          </DialogHeader>

          {selectedItem ? (
            <div className="flex-1 space-y-4 overflow-y-auto pr-1">
              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-1">
                  <Label>Full Name</Label>
                  <Input value={resolveString(selectedItem.fullName, "-")} disabled />
                </div>
                <div className="space-y-1">
                  <Label>Username</Label>
                  <Input value={resolveString(selectedItem.username, selectedItem.email, "-")} disabled />
                </div>
                <div className="space-y-1">
                  <Label>Email</Label>
                  <Input value={resolveString(selectedItem.email, "-")} disabled />
                </div>
                <div className="space-y-1">
                  <Label>Contractor Company</Label>
                  <Input value={resolveString(selectedItem.companyName, "-")} disabled />
                </div>
                <div className="space-y-1">
                  <Label>Position</Label>
                  <Input value={resolveString(selectedItem.position, "-")} disabled />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input value={resolveString(selectedItem.phone, "-")} disabled />
                </div>
              </div>

              <div className="space-y-1">
                <Label>Remark</Label>
                <Textarea value={selectedItem.remark ?? ""} placeholder="No remark" rows={2} className="min-h-0" disabled />
              </div>

              <div className="space-y-2">
                <Label>Tenants * (multi-select)</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-between">
                      <span className="truncate text-left">{selectedTenantsLabel}</span>
                      <span className="text-xs text-slate-500">{approveTenantIds.length} selected</span>
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[360px] space-y-2">
                    <Input
                      placeholder="Search tenant..."
                      value={approveTenantSearch}
                      onChange={(event) => setApproveTenantSearch(event.target.value)}
                    />
                    <div className="max-h-56 space-y-1 overflow-y-auto pr-1">
                      {isLoadingTenants ? (
                        <div className="py-2 text-sm text-gray-500">Loading tenants...</div>
                      ) : filteredTenants.length === 0 ? (
                        <div className="py-2 text-sm text-gray-500">No tenants found</div>
                      ) : (
                        filteredTenants.map((item) => {
                          const checked = approveTenantIds.includes(item.tenantId)
                          return (
                            <label
                              key={item.tenantId}
                              className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 hover:bg-gray-50"
                            >
                              <Checkbox
                                checked={checked}
                                onCheckedChange={(next) => {
                                  setApproveTenantIds((prev) => {
                                    if (next) {
                                      if (prev.includes(item.tenantId)) return prev
                                      return [...prev, item.tenantId]
                                    }
                                    return prev.filter((tenantId) => tenantId !== item.tenantId)
                                  })
                                }}
                              />
                              <span className="text-sm">{item.name}</span>
                            </label>
                          )
                        })
                      )}
                    </div>
                  </PopoverContent>
                </Popover>
              </div>

              <div className="space-y-2">
                <Label>Reject reason (optional)</Label>
                <Textarea
                  rows={2}
                  className="min-h-0"
                  placeholder="Enter reason for rejecting this request..."
                  value={rejectRemark}
                  onChange={(event) => setRejectRemark(event.target.value)}
                  disabled={isApproving || isRejecting}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseApproveDialog} disabled={isApproving || isRejecting}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleReject} disabled={!selectedItem || isApproving || isRejecting}>
              {isRejecting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Reject
            </Button>
            <Button onClick={handleApprove} disabled={!selectedItem || approveTenantIds.length === 0 || isApproving || isRejecting}>
              {isApproving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(inactiveItem)} onOpenChange={(open) => !open && handleCloseInactiveDialog()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Set Contractor Inactive</DialogTitle>
            <DialogDescription>Set this contractor account to inactive status. Reason is optional.</DialogDescription>
          </DialogHeader>

          {inactiveItem ? (
            <div className="space-y-4">
              <div className="space-y-1">
                <Label>Username</Label>
                <Input value={resolveString(inactiveItem.username, inactiveItem.email, "-")} disabled />
              </div>
              <div className="space-y-1">
                <Label>Reason (optional)</Label>
                <Textarea
                  rows={4}
                  placeholder="Enter reason for inactive..."
                  value={inactiveRemark}
                  onChange={(event) => setInactiveRemark(event.target.value)}
                />
              </div>
            </div>
          ) : null}

          <DialogFooter>
            <Button variant="outline" onClick={handleCloseInactiveDialog} disabled={isInactivating}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleInactive} disabled={!inactiveItem || isInactivating}>
              {isInactivating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Confirm Inactive
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
